import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erStreng} from "felles-ng2/støttefunksjoner/strenger";
import {filendelseFraFile} from "felles-ng2/støttefunksjoner/filer";

/** Returnerer mimeinfo basert på angitt mimetype.
 * Returnerer null dersom vi ikke støtter angitt mimetype. */
export function finnMimeinfoBasertPåMimetype(mimetype: string): Mimeinfo | null {
    return mimetyperSomListe().find(kandidat => kandidat.mimetype == mimetype) ?? null;
}

/** Returnerer mimeinfo basert på angitt mimetype.
 * Returnerer en fallback mimeinfo representasjon av angitt mimetype dersom vi ikke støtter angitt mimetype. */
export function finnMimeinfoBasertPåMimetypeMedFallback(mimetype: string): Mimeinfo {
    const mimeinfo = finnMimeinfoBasertPåMimetype(mimetype);
    if (erIkkeNil(mimeinfo)) return mimeinfo;
    return {
        mimetype: mimetype,
        appleUti: [],
        filendelse: [],
        visningsnavn: mimetype,
        ikon: 'lederplan-ikon-dokument'
    }
}

/** Returnerer mimeinfo basert på angitt filendelse.
 * Returnerer null dersom angitt filendelse representerer en mimetype vi ikke støtter. */
export function finnMimeinfoBasertPåFilendelse(filendelse: string | File): Mimeinfo | null {
    if (!erStreng(filendelse)) filendelse = filendelseFraFile(filendelse);
    const mimeinfo = mimetyperSomListe().find(kandidat => kandidat.filendelse.includes(filendelse));
    return mimeinfo ?? null;
}

/** Returnerer mimeinfo for angitt fil.
 * Forsøker først å finne mimeinfo ved å se på {@link File#type}, ser så på filendelse.
 * Returnerer null dersom angitt filendelse representerer en mimetype vi ikke støtter. */
export function finnMimeinfoForFil(fil: File): Mimeinfo | null {
    return finnMimeinfoBasertPåMimetype(fil.type) || finnMimeinfoBasertPåFilendelse(fil);
}

/** Alle støttede mimetyper i listeform */
export function mimetyperSomListe(): Mimeinfo[] {
    return [...Object.values(mimetyper)];
}

/** Returnerer en streng som kan brukes som verdi for input[type=file].accept attributet.
 * Vil dermed sørge for at filvelger-dialog kun lar bruker velge blant filer med mimetype som
 * anigtt via 'mimetyper'. */
export function mimetyperSomFilInputAccept(mimetyper:Mimeinfo[]): string {
    return mimetyper
      .flatMap(mimeinfo => [mimeinfo.mimetype, ...mimeinfo.filendelse.map(endelse => '.' + endelse)])
      .join(',')
}

export function tillatteMimetyper(): Mimeinfo[] {
    return [
        mimetyper.pdf,
        mimetyper.txt,
        mimetyper.rtf,
        mimetyper.sdo,
        mimetyper.png,
        mimetyper.tiff,
        mimetyper.gif,
        mimetyper.jpeg,
        mimetyper.xls,
        mimetyper.xlsx,
        mimetyper.ppt,
        mimetyper.pptx,
        mimetyper.ppsx,
        mimetyper.doc,
        mimetyper.docx,
        mimetyper.odt,
        mimetyper.ods,
        mimetyper.odp,
        mimetyper.zip
    ]
}

export function tillatteBildeMimetyper(): Mimeinfo[] {
    return [mimetyper.jpeg, mimetyper.png, mimetyper.gif, mimetyper.svg];
}

/**
 * Tillatte mimetyper.
 * Må holdes i synk med no.systemfabrikken.lederplan.tjenester.dokumenter.TillateMimetyper,
 * ref no.systemfabrikken.lederplan.domene.dokument.OpplastetDokumentVersjon.OpplastetDokumentVersjonBygger.valider
 * Se http://filext.com/faq/office_mime_types.php for office typer
 * Se https://developer.apple.com/library/archive/documentation/Miscellaneous/Reference/UTIRef/Articles/System-DeclaredUniformTypeIdentifiers.html for UTI-typer
 */
export const mimetyper/*: { [type: string]: Mimeinfo } */ = {
    pdf: {
        mimetype: 'application/pdf',
        appleUti: ['com.adobe.pdf'],
        filendelse: ['pdf'],
        visningsnavn: 'PDF',
        ikon: 'lederplan-ikon-pdf'
    },

    txt: {
        mimetype: 'text/plain',
        appleUti: ['public.text'],
        filendelse: ['txt'],
        visningsnavn: 'Text',
        ikon: 'lederplan-ikon-dokument'
    },

    rtf: {
        mimetype: 'application/rtf',
        appleUti: ['public.text'],
        filendelse: ['rtf'],
        visningsnavn: 'Word',
        ikon: 'lederplan-ikon-word'
    },

    sdo: {
        mimetype: 'application/x-seid-sdo',
        appleUti: ['public.xml'],
        filendelse: ['sdo'],
        visningsnavn: 'SDO',
        ikon: 'lederplan-ikon-dokument'
    },

    png: {
        mimetype: 'image/png',
        appleUti: ['public.image'],
        filendelse: ['png'],
        visningsnavn: 'png',
        ikon: 'lederplan-ikon-dokument'
    },

    tiff: {
        mimetype: 'image/tiff',
        appleUti: ['public.image'],
        filendelse: ['tiff'],
        visningsnavn: 'tiff',
        ikon: 'lederplan-ikon-dokument'
    },

    gif: {
        mimetype: 'image/gif',
        appleUti: ['public.image'],
        filendelse: ['gif'],
        visningsnavn: 'gif',
        ikon: 'lederplan-ikon-dokument'
    },

    jpeg: {
        mimetype: 'image/jpeg',
        appleUti: ['public.image'],
        filendelse: ['jpg', 'jpeg'],
        visningsnavn: 'jpg',
        ikon: 'lederplan-ikon-dokument'
    },

    svg: {
        mimetype: 'image/svg+xml',
        appleUti: ['public.image'],
        filendelse: ['svg'],
        visningsnavn: 'svg',
        ikon: 'lederplan-ikon-dokument'
    },

    //ms excel
    xls: {
        mimetype: 'application/vnd.ms-excel',
        appleUti: ['com.microsoft.excel.xls'],
        filendelse: ['xls'],
        visningsnavn: 'Excel',
        ikon: 'lederplan-ikon-excel'
    },
    xlsx: {
        mimetype: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        appleUti: ['org.openxmlformats.spreadsheetml.sheet'],
        filendelse: ['xlsx'],
        visningsnavn: 'Excel',
        ikon: 'lederplan-ikon-excel'
    },

    //ms powerpoint
    ppt: {
        mimetype: 'application/vnd.ms-powerpoint',
        appleUti: ['com.microsoft.powerpoint.ppt'],
        filendelse: ['ppt'],
        visningsnavn: 'PPT',
        ikon: 'lederplan-ikon-ppt'
    },
    pptx: {
        mimetype: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        appleUti: ['org.openxmlformats.presentationml.presentation'],
        filendelse: ['pptx'],
        visningsnavn: 'PPT',
        ikon: 'lederplan-ikon-ppt'
    },
    ppsx: {
        mimetype: 'application/application/vnd.openxmlformats-officedocument.presentationml.slideshow',
        appleUti: ['org.openxmlformats.presentationml.slideshow'],
        filendelse: ['ppsx'],
        visningsnavn: 'PPT',
        ikon: 'lederplan-ikon-ppt'
    },

    //ms word
    doc: {
        mimetype: 'application/msword',
        appleUti: ['com.microsoft.word.doc'],
        filendelse: ['doc'],
        visningsnavn: 'Word',
        ikon: 'lederplan-ikon-word'
    },
    docx: {
        mimetype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        appleUti: ['com.microsoft.word.wordml', 'org.openxmlformats.wordprocessingml.document'],
        filendelse: ['docx'],
        visningsnavn: 'Word',
        ikon: 'lederplan-ikon-word'
    },
    //openoffice mimetyper, stiler disse som office-dokumenter
    odt: {
        mimetype: 'application/vnd.oasis.opendocument.text',
        appleUti: ['com.microsoft.word.wordml', 'org.openxmlformats.wordprocessingml.document'],
        filendelse: ['odt'],
        visningsnavn: 'Word',
        ikon: 'lederplan-ikon-word'
    },
    ods: {
        mimetype: 'application/vnd.oasis.opendocument.spreadsheet',
        appleUti: ['org.openxmlformats.spreadsheetml.sheet'],
        filendelse: ['ods'],
        visningsnavn: 'Excel',
        ikon: 'lederplan-ikon-excel'
    },
    odp: {
        mimetype: 'application/vnd.oasis.opendocument.presentation',
        appleUti: ['org.openxmlformats.presentationml.presentation'],
        filendelse: ['odp'],
        visningsnavn: 'PPT',
        ikon: 'lederplan-ikon-ppt'
    },
    zip: {
        mimetype: 'application/zip',
        appleUti: ['com.pkware.zip-archive'],
        filendelse: ['zip'],
        visningsnavn: 'ZIP',
        ikon: 'lederplan-ikon-zip'
    },
};

export const fallbackMimetype: Mimeinfo = {
    mimetype: 'application/octet-stream',
    appleUti: [],
    filendelse: [],
    visningsnavn: 'Ukjent',
    ikon: 'lederplan-ikon-dokument'
};


export interface Mimeinfo {
    mimetype: string;
    appleUti: string[];
    filendelse: string[];
    visningsnavn: string;
    ikon: string;
}
